/**
 * Task 151-B — a single package card for the packages grid (the primary
 * surface). Shows the package name + description, its price + billing
 * cadence, the included-service chips, and (when any `ads`-kind line has
 * platforms) the ad-platform chips — plus an edit affordance for
 * managers. Read-only / display; all editing happens in the drawer.
 */

import type { AgencyPackageRow } from '../../api/types';
import { billingIntervalLabel } from './helpers';
import { isPackageServiceLineVisible } from './launchVisibility';
import { packageAdPlatforms } from './packageDrawerModel';
import { AD_PLATFORM_LABELS } from './serviceConfigSections/labels';

export function PackageCard({
  pkg,
  canManage,
  onEdit,
  onArchive,
  onUnarchive,
}: {
  readonly pkg: AgencyPackageRow;
  readonly canManage: boolean;
  readonly onEdit: () => void;
  /** Manager-only soft-archive (sets is_active=false). Active packages only. */
  readonly onArchive?: () => void;
  /** Manager-only restore (sets is_active=true). Archived packages only. */
  readonly onUnarchive?: () => void;
}) {
  const adPlatforms = packageAdPlatforms(pkg);
  const includedServices = pkg.services.filter((s) => s.included && isPackageServiceLineVisible(s));

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      {/* Head */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: 18, margin: 0 }}>{pkg.name}</h3>
            <span className={`pill pill-${pkg.is_active ? 'active' : 'archived'}`}>
              {pkg.is_active ? 'نشطة' : 'موقوفة'}
            </span>
          </div>
          {pkg.description ? (
            <p className="tiny" style={{ color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
              {pkg.description}
            </p>
          ) : null}
        </div>
        {canManage ? (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button
              type="button"
              className="btn-text"
              onClick={onEdit}
              style={{ padding: '6px 12px' }}
            >
              تعديل
            </button>
            {pkg.is_active && onArchive ? (
              <button
                type="button"
                className="btn-text"
                onClick={onArchive}
                style={{ padding: '6px 12px', color: 'var(--rose)' }}
              >
                أرشفة
              </button>
            ) : null}
            {!pkg.is_active && onUnarchive ? (
              <button
                type="button"
                className="btn-text"
                onClick={onUnarchive}
                style={{ padding: '6px 12px' }}
              >
                استعادة
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Price */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
          paddingBottom: 14,
          borderBottom: '1px solid var(--line)',
        }}
      >
        {pkg.price_amount !== null ? (
          <>
            <strong style={{ fontSize: 30, color: 'var(--tangerine)' }} dir="ltr">
              {pkg.price_amount}
            </strong>
            <span style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 600 }}>
              {pkg.price_currency}
            </span>
          </>
        ) : (
          <strong style={{ fontSize: 18, color: 'var(--muted)' }}>بدون سعر</strong>
        )}
        <span className="chip" style={{ fontSize: 11 }}>
          {billingIntervalLabel(pkg.billing_interval)}
        </span>
      </div>

      {/* Included services */}
      <div>
        <div className="lbl" style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
          الخدمات المضمنة
        </div>
        {includedServices.length > 0 ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {includedServices.map((s) => (
              <span key={s.service_id} className="chip" style={{ fontSize: 11.5 }}>
                {s.service_name}
              </span>
            ))}
          </div>
        ) : (
          <p className="tiny" style={{ color: 'var(--muted)' }}>
            لا توجد خدمات مضمّنة بعد.
          </p>
        )}
      </div>

      {/* Ad platforms (only when an ads-kind line has platforms) */}
      {adPlatforms.length > 0 ? (
        <div>
          <div className="lbl" style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
            منصات الإعلانات
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {adPlatforms.map((k) => (
              <span key={k} className="pill pill-info" style={{ fontSize: 10.5 }}>
                {AD_PLATFORM_LABELS[k]}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
